/** Shared card previews. Original images remain owned by the theme/lazy loader. */
(() => {
  if (window.thlSwatchPreview) return;
  window.thlSwatchPreview = true;
  const selector = '[data-variant-image][data-variant-id]';
  const states = new WeakMap();
  const requests = new Map();
  const queue = [];
  let backgroundCount = 0;
  const hover = () => window.matchMedia('(any-hover: hover)').matches;
  const economical = () => {
    const connection = navigator.connection;
    return connection?.saveData || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '');
  };
  function stateFor(card) {
    if (!states.has(card)) states.set(card, { generation: 0, target: null, layers: [], originals: null });
    return states.get(card);
  }
  function imageUrl(card, swatch) {
    const url = new URL(swatch.dataset.variantImage, location.origin);
    const width = card.querySelector('.product-item__image-link')?.getBoundingClientRect().width || 360;
    const desired = width * (window.devicePixelRatio || 1);
    const bucket = [360, 540, 720, 900, 1080, 1440].find(value => value >= desired) || 1440;
    url.searchParams.set('width', Math.min(bucket, Number(swatch.dataset.variantImageWidth) || 1440));
    return url.href;
  }
  function pump() {
    while (backgroundCount < 2 && queue.length) {
      const entry = queue.shift();
      if (!entry.started) start(entry, true);
    }
  }
  function start(entry, background) {
    entry.started = true;
    if (background) backgroundCount++;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = background ? 'low' : 'auto';
    entry.image = image;
    const finish = async () => {
      image.onload = image.onerror = null;
      try {
        if (!image.naturalWidth) throw new Error('Image unavailable');
        if (image.decode) await image.decode();
        entry.done = true;
        entry.resolve(image);
      } catch (error) {
        requests.delete(entry.url);
        entry.reject(error);
      } finally {
        if (background) backgroundCount--;
        // Bound retained decoded image references; the browser owns the HTTP cache.
        if (requests.size > 80) {
          for (const [url, item] of requests) {
            if (item.done) requests.delete(url);
            if (requests.size <= 80) break;
          }
        }
        pump();
      }
    };
    image.onload = finish;
    image.onerror = finish;
    image.src = entry.url;
  }
  function load(url, background = false) {
    let entry = requests.get(url);
    if (!entry) {
      entry = { url, started: false, done: false };
      entry.promise = new Promise((resolve, reject) => Object.assign(entry, { resolve, reject }));
      requests.set(url, entry);
      if (background) queue.push(entry);
    }
    if (!background && !entry.started) start(entry, false);
    else if (!background && entry.image) entry.image.fetchPriority = 'auto';
    if (background) pump();
    return entry.promise;
  }
  function prewarm(card, limit = Infinity) {
    if (!hover() || economical()) return;
    const unique = new Set();
    for (const item of card.querySelectorAll('.product-item-options__item')) {
      const swatch = item.matches(selector) ? item : item.querySelector(selector);
      if (!swatch || !item.getClientRects().length) continue;
      const url = imageUrl(card, swatch);
      if (unique.has(url)) continue;
      unique.add(url);
      load(url, true).catch(() => {});
      if (unique.size >= limit) break;
    }
  }
  function remember(card, state) {
    if (state.originals) return;
    state.originals = [];
    for (const element of card.querySelectorAll('.product-item__image-link[href], .product-item__product-title a')) {
      state.originals.push({ element, attribute: 'href', value: element.getAttribute('href') });
    }
    const button = card.querySelector('.product-item__quick-add-button');
    if (button) state.originals.push({ element: button, attribute: 'data-product-id', value: button.getAttribute('data-product-id') });
    const select = card.querySelector('[id="variant-selector"]');
    if (select) state.originals.push({ element: select, value: select.value });
  }
  function updateLinks(card, state, variant) {
    remember(card, state);
    for (const original of state.originals) {
      if (original.attribute === 'href') {
        const url = new URL(original.value, location.href);
        url.searchParams.set('variant', variant);
        original.element.setAttribute('href', url.href);
      } else if (original.attribute) original.element.setAttribute(original.attribute, variant);
      else original.element.value = variant;
    }
  }
  function restore(card) {
    const state = states.get(card);
    if (!state) return;
    state.generation++;
    state.target = null;
    state.layers.forEach(layer => layer.remove());
    state.layers = [];
    for (const original of state.originals || []) {
      if (!original.attribute) original.element.value = original.value;
      else if (original.value === null) original.element.removeAttribute(original.attribute);
      else original.element.setAttribute(original.attribute, original.value);
    }
    state.originals = null;
  }
  async function preview(card, swatch) {
    const state = stateFor(card);
    const url = imageUrl(card, swatch);
    const target = `${swatch.dataset.variantId}:${url}`;
    if (state.target === target) return;
    state.target = target;
    const generation = ++state.generation;
    try {
      await load(url);
      if (state.generation !== generation || !card.isConnected) return;
      const container = card.querySelector('.product-item__image-link');
      if (!container) return;
      const layer = document.createElement('img');
      layer.className = 'thl-swatch-preview';
      layer.alt = swatch.dataset.variantImageAlt || '';
      layer.decoding = 'async';
      layer.src = url;
      // Decode this display element too, before attaching it over the previous image.
      if (layer.decode) await layer.decode();
      if (state.generation !== generation || !card.isConnected) return;
      state.layers.forEach(old => old.classList.remove('thl-swatch-preview--enter'));
      container.appendChild(layer);
      state.layers.push(layer);
      while (state.layers.length > 2) state.layers.shift().remove();
      layer.classList.add('thl-swatch-preview--enter');
      updateLinks(card, state, swatch.dataset.variantId);
      setTimeout(() => {
        if (state.layers.at(-1) !== layer) return;
        state.layers.slice(0, -1).forEach(old => old.remove());
        state.layers = [layer];
      }, 140);
    } catch (_) {
      if (state.generation === generation) state.target = null;
    }
  }
  document.addEventListener('pointerover', event => {
    if (event.pointerType === 'touch' || !hover()) return;
    const card = event.target.closest?.('.product-item');
    if (!card) return;
    const swatch = event.target.closest(selector);
    if (swatch && card.contains(swatch) && !swatch.contains(event.relatedTarget)) preview(card, swatch);
    if (!card.contains(event.relatedTarget)) prewarm(card);
  });
  document.addEventListener('pointerout', event => {
    const card = event.target.closest?.('.product-item');
    if (card && !card.contains(event.relatedTarget)) restore(card);
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.product-item').forEach(restore);
  });
  // Delegation handles new cards immediately; observation only schedules prewarming.
  function observeCards() {
    if (!('IntersectionObserver' in window) || !hover() || economical()) return;
    const observed = new WeakSet();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        prewarm(entry.target, 2);
      });
    });
    const scan = root => {
      const cards = root.matches?.('.product-item') ? [root] : root.querySelectorAll?.('.product-item') || [];
      cards.forEach(card => {
        if (observed.has(card)) return;
        observed.add(card);
        observer.observe(card);
      });
    };
    scan(document);
    new MutationObserver(records => records.forEach(record => {
      record.removedNodes.forEach(node => {
        if (node.nodeType !== 1 || node.isConnected) return;
        const cards = node.matches('.product-item') ? [node] : node.querySelectorAll('.product-item');
        cards.forEach(card => { observer.unobserve(card); observed.delete(card); restore(card); });
      });
      record.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); });
    })).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'complete') observeCards();
  else window.addEventListener('load', observeCards, { once: true });
})();
